import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getGallery, removeGallery, updateGallery } from 'slice/gallerySlice';
import AdminTable from 'common/AdminTable';
import { toast } from 'react-toastify';

const getImageUrl = (image) => {
  if (!image) return null;
  if (/^https?:\/\//i.test(image)) return image;

  const backendBase = (process.env.REACT_APP_BACKEND || '').replace(/\/+$/, '');
  if (!backendBase) return image;

  const normalizedPath = String(image).replace(/^\/+/, '');
  const fullPath = normalizedPath.startsWith('uploads/')
    ? normalizedPath
    : `uploads/gallery/${normalizedPath}`;
  return `${backendBase}/${fullPath}`;
};

const List = () => {
  const dispatch = useDispatch();
  const { data: galleryList, loading } = useSelector((state) => state.gallery);

  const [showModal, setShowModal] = useState(false);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    image: null,
    existingImage: ''
  });

  useEffect(() => {
    dispatch(getGallery());
  }, [dispatch]);

  // 🔹 Open modal on Edit
  const handleEditClick = (item) => {
    setSelectedGallery(item);
    setFormData({
      name: item.name,
      image: null,
      existingImage: item.image ? getImageUrl(item.image) : ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedGallery(null);
  };

  const handleFormChange = React.useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (prev[name] === value) return prev;
      return { ...prev, [name]: value };
    });
  }, []);

  const handleFileChange = (e) => {
    const { files } = e.target;
    setFormData((prev) => ({ ...prev, image: files[0] }));
  };

  const handleSaveChanges = () => {
    const updatedData = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (key === 'image') {
        if (value) updatedData.append(key, value);
      } else if (!key.startsWith('existing')) {
        updatedData.append(key, value);
      }
    });

    dispatch(
      updateGallery({
        id: selectedGallery.id,
        data: updatedData
      })
    );

    toast.success('Gallery updated successfully');
    closeModal();
  };

  const handleRemove = (id) => {
    if (!window.confirm('Delete this gallery item?')) return;
    dispatch(removeGallery(id));
    toast.success('Gallery removed successfully');
  };

  const tableHeaders = [
    { Header: 'ID', accessor: 'id' },
    { Header: 'Name', accessor: 'name' },
    {
      Header: 'Image',
      accessor: 'image',
      Cell: ({ row }) =>
        row.original.image ? (
          <img src={getImageUrl(row.original.image)} alt={row.original.name} width={100} />
        ) : (
          'No Image'
        )
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <>
          <button className="btn btn-danger mx-1" onClick={() => handleRemove(row.original.id)}>
            Remove
          </button>

          <button className="btn btn-primary mx-1" onClick={() => handleEditClick(row.original)}>
            Edit
          </button>
        </>
      )
    }
  ];

  return (
    <>
      <div className="container-fluid mt-5">
        <div className="mainheadig mx-4">
          <h4 className="text-white font-weight-bold">Gallery List</h4>
        </div>

        <AdminTable tableHeaders={tableHeaders} tableData={galleryList || []} />

        {loading && <p className="text-white mx-4 mt-2">Updating gallery...</p>}

        {!loading && galleryList?.length === 0 && <p className="text-white mx-4">No gallery found.</p>}
      </div>

      {/* 🔥 MODAL — EXACT LIKE HIGHLIGHT */}
      {showModal && selectedGallery && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Edit Gallery</h5>
                <button type="button" className="close" onClick={closeModal}>
                  <span>&times;</span>
                </button>
              </div>

              <div className="modal-body">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  placeholder="Title"
                  className="form-control mb-2"
                />

                <input type="file" name="image" onChange={handleFileChange} className="form-control mb-2" />

                {formData.image ? (
                  <img src={URL.createObjectURL(formData.image)} width={100} alt="preview" />
                ) : (
                  <img src={formData.existingImage} width={100} alt="existing" />
                )}
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeModal}>
                  Close
                </button>
                <button className="btn btn-primary" onClick={handleSaveChanges}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default List;
